import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AppView } from '../types';
import type { TutorialKapitelId, TutorialRolle } from '../tutorial/types';
import { kapitelNachId } from '../tutorial/register';
import {
    TOUR_SCHLUESSEL,
    WILLKOMMEN_SCHLUESSEL,
    lieseTourUebergabe,
    sollWillkommensTourStarten,
    willkommensKapitel,
} from '../components/lernzentrum/tourLogic';

/**
 * LERNZENTRUM — wer die Tour startet und wann sie endet (Paket B).
 *
 * Zwei Einstiege, ein Zustand:
 *
 *  1. **„Jetzt in der App zeigen"** auf der Kapitelseite (Auftrag A3) legt
 *     Kapitel-ID und Zeitstempel in `sessionStorage['lernzentrum.tour']` und
 *     wechselt die Ansicht. Dieser Hook holt sie ab, sobald die Ansicht
 *     stimmt — nicht früher: sonst zeigt die Tour auf Anker, die im
 *     Lernzentrum gar nicht im DOM stehen. Passt die Ansicht noch nicht,
 *     bleibt der Schlüssel liegen und der nächste `view`-Wechsel versucht es
 *     erneut — aber nur eine Minute lang (`lieseTourUebergabe`). Danach ist
 *     der Wert verfallen und wird weggeräumt: eine Tour, die eine halbe
 *     Stunde später aus dem Nichts anspringt, hat mit dem Klick, der sie
 *     angefordert hat, nichts mehr zu tun.
 *  2. **Willkommens-Tour** beim ersten Mal. Sie hängt seit Migration Nr. 102
 *     am NUTZER (`profiles.ersteinweisung_am`, Option `ersteinweisungAm`),
 *     nicht mehr am Gerät; `localStorage['lernzentrum.willkommen']` ist nur
 *     noch der Zwischenspeicher gegen einen zweiten Start, solange das
 *     Schreiben in die Datenbank unterwegs ist oder gar nicht geht.
 *
 * ─── Warum der Hook keinen Service kennt ────────────────────────────────────
 *
 * `markiereAbgeschlossen` kommt als Callback herein, nicht als Import. Damit
 * hängt die Tour nicht an Supabase, ist ohne Netz prüfbar, und der Container
 * entscheidet, ob ein Abschluss überhaupt protokolliert wird (im
 * Übungsprojekt z. B. nicht). Die Entscheidung selbst
 * (`sollWillkommensTourStarten`) liegt in `tourLogic.ts` und ist dort getestet.
 */

export interface TourOptionen {
    /** Die Ansicht, in der die App gerade steht. */
    view: AppView;
    /** Lernrollen des angemeldeten Profils (`rollenAusProfil`). */
    rollen: TutorialRolle[];
    /**
     * Darf die Willkommens-Tour von selbst anlaufen? Default `true`. Auf
     * `false` setzen, solange das Profil lädt, ein Modal offen ist oder der
     * mobile Wizard läuft — eine Tour über einer halb aufgebauten Oberfläche
     * findet ihre Anker nicht.
     */
    willkommenErlaubt?: boolean;
    /**
     * `profiles.ersteinweisung_am` des angemeldeten Nutzers — die FÜHRENDE
     * Quelle für „Willkommens-Tour schon gehabt?" (Migration Nr. 102).
     *
     * `null` heisst „steht noch aus", ein Zeitstempel „erledigt, nie wieder
     * von selbst", `undefined` heisst „Spalte (oder Profil) noch nicht da" —
     * dann entscheidet wie früher allein der Browser-Merker.
     */
    ersteinweisungAm?: string | null;
    /**
     * Die Willkommens-Tour ist vorbei (durchlaufen ODER weggeklickt): den
     * Merker am NUTZER festhalten. Der Container schreibt
     * `profiles.ersteinweisung_am` und zieht das Profil in seinem Zustand
     * nach; dieser Hook kennt Supabase nicht.
     */
    merkeErsteinweisung?: () => void;
    /** Tour bis zum letzten Schritt durchlaufen. */
    markiereAbgeschlossen?: (id: TutorialKapitelId) => void;
    /** Abgebrochen — Rohstoff für `tutorial_fortschritt.tour_abgebrochen_bei`. */
    merkeAbbruch?: (id: TutorialKapitelId, schrittIndex: number) => void;
}

export interface TourSteuerung {
    /** Kapitel-ID für `<TourOverlay kapitelId={…} />`, sonst `null`. */
    aktiveTourId: TutorialKapitelId | null;
    starteTour: (id: TutorialKapitelId) => void;
    /** Passt auf `TourOverlayProps['onEnde']`. */
    beendeTour: (ergebnis: 'fertig' | 'abgebrochen', schrittIndex: number) => void;
}

// ─── Speicher, der nie wirft ─────────────────────────────────────────────────
// Safari im privaten Modus wirft beim blossen Lesen. Eine Hilfe darf daran
// nicht die App mitreissen — im Zweifel gibt es eben keine Tour.

const lies = (speicher: 'session' | 'local', schluessel: string): string | null => {
    try {
        const s = speicher === 'session' ? window.sessionStorage : window.localStorage;
        return s.getItem(schluessel);
    } catch {
        return null;
    }
};

const schreibe = (speicher: 'session' | 'local', schluessel: string, wert: string): void => {
    try {
        const s = speicher === 'session' ? window.sessionStorage : window.localStorage;
        s.setItem(schluessel, wert);
    } catch {
        /* kein Speicher — dann eben jedes Mal wieder. */
    }
};

const loesche = (speicher: 'session' | 'local', schluessel: string): void => {
    try {
        const s = speicher === 'session' ? window.sessionStorage : window.localStorage;
        s.removeItem(schluessel);
    } catch {
        /* siehe oben */
    }
};

/**
 * Eine Tour erst nach dem Bild starten, in dem die neue Ansicht steht.
 *
 * Zwei Gründe, beide praktisch: Die Anker der Zielansicht stehen im Moment des
 * `view`-Wechsels noch nicht im DOM — das Overlay würde seine Suche mit einem
 * garantierten Fehlschlag beginnen. Und ein synchrones `setState` im
 * Effektrumpf ist in diesem Repo verboten (Regel
 * `react-hooks/set-state-in-effect`, CI-Ratsche). Rückgabe ist der Abbruch.
 */
const nachDemRendern = (tu: () => void): (() => void) => {
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
        const t = setTimeout(tu, 0);
        return () => clearTimeout(t);
    }
    const id = window.requestAnimationFrame(tu);
    return () => window.cancelAnimationFrame(id);
};

// ─── Hook ────────────────────────────────────────────────────────────────────

export const useTour = (optionen: TourOptionen): TourSteuerung => {
    const { view, rollen, willkommenErlaubt = true, ersteinweisungAm } = optionen;

    const [aktiveTourId, setAktiv] = useState<TutorialKapitelId | null>(null);

    const aktivRef = useRef<TutorialKapitelId | null>(null);
    const optRef = useRef(optionen);
    const willkommenIdRef = useRef<TutorialKapitelId | null>(null);
    const willkommenVersuchtRef = useRef(false);

    useEffect(() => {
        optRef.current = optionen;
    });
    useEffect(() => {
        aktivRef.current = aktiveTourId;
    }, [aktiveTourId]);

    /** Stabiler Vergleichswert — das Array wechselt bei jedem Render die Identität. */
    const rollenSchluessel = rollen.join(',');
    const rollenListe = useMemo(
        () => (rollenSchluessel ? (rollenSchluessel.split(',') as TutorialRolle[]) : []),
        [rollenSchluessel],
    );

    // ── Übergabe aus dem Lernzentrum ─────────────────────────────────────────
    useEffect(() => {
        if (aktiveTourId) return;
        const uebergabe = lieseTourUebergabe(lies('session', TOUR_SCHLUESSEL), Date.now());
        if (uebergabe.art === 'verfallen') {
            // Zielansicht nie erreicht, Tab lange offen, alte Schreibweise:
            // wegräumen statt später unvermittelt anspringen.
            loesche('session', TOUR_SCHLUESSEL);
            return;
        }
        if (uebergabe.art !== 'gueltig') return;
        const id = uebergabe.id;

        const kapitel = kapitelNachId(id);
        if (!kapitel) {
            // Unbekannte ID: Schlüssel weg, sonst hängt er die ganze Sitzung
            // an jedem Ansichtswechsel und versucht es wieder.
            loesche('session', TOUR_SCHLUESSEL);
            return;
        }
        // Kapitel ohne feste Ansicht starten überall; sonst warten wir, bis
        // A3 die Ansicht gewechselt hat.
        if (kapitel.view && kapitel.view !== view) return;

        // Schlüssel und Start liegen zusammen im Rückruf: würde hier gelöscht
        // und der Rückruf danach abgebrochen (Ansichtswechsel im selben Bild),
        // wäre die angeforderte Tour spurlos weg.
        return nachDemRendern(() => {
            loesche('session', TOUR_SCHLUESSEL);
            setAktiv(id);
        });
    }, [view, aktiveTourId]);

    // ── Willkommens-Tour ─────────────────────────────────────────────────────
    useEffect(() => {
        if (!willkommenErlaubt || aktiveTourId || willkommenVersuchtRef.current) return;
        if (!sollWillkommensTourStarten(!!lies('local', WILLKOMMEN_SCHLUESSEL), rollenListe, ersteinweisungAm)) return;
        // Eine angeforderte Tour hat Vorrang vor der Begrüssung — eine
        // verfallene aber nicht, sonst blockierte ein Rest im sessionStorage
        // die Begrüssung für den Rest der Sitzung.
        if (lieseTourUebergabe(lies('session', TOUR_SCHLUESSEL), Date.now()).art === 'gueltig') return;

        const id = willkommensKapitel(rollenListe);
        const kapitel = id ? kapitelNachId(id) : undefined;
        if (!id || !kapitel) return;
        // Erst in der richtigen Ansicht — die Anker der Begrüssung stehen im
        // Dashboard und in der Seitenleiste.
        if (kapitel.view && kapitel.view !== view) return;

        return nachDemRendern(() => {
            willkommenVersuchtRef.current = true;
            willkommenIdRef.current = id;
            setAktiv(id);
        });
    }, [view, aktiveTourId, rollenListe, willkommenErlaubt, ersteinweisungAm]);

    const starteTour = useCallback((id: TutorialKapitelId) => {
        if (!kapitelNachId(id)) return;
        loesche('session', TOUR_SCHLUESSEL);
        aktivRef.current = id;
        setAktiv(id);
    }, []);

    const beendeTour = useCallback<TourSteuerung['beendeTour']>((ergebnis, schrittIndex) => {
        const id = aktivRef.current;
        aktivRef.current = null;
        setAktiv(null);
        if (!id) return;

        const opt = optRef.current;

        // Nach Ende ODER Abbruch: die Begrüssung läuft nie wieder von selbst.
        // Wer sie noch einmal will, startet sie im Lernzentrum.
        //
        // ZWEI Merker, und beide werden gebraucht:
        //  · `localStorage` wirkt sofort und ohne Netz. Er verhindert den
        //    zweiten Start in der Zeit, die das Schreiben in die Datenbank
        //    braucht — und wenn dieses Schreiben scheitert (Migration Nr. 102
        //    noch nicht gefahren), ist er der einzige Merker, den es gibt.
        //  · `merkeErsteinweisung` hält es am NUTZER fest. Nur das übersteht
        //    einen anderen Browser, ein anderes Gerät und einen geleerten
        //    Speicher — und nur das lässt den zweiten Nutzer an demselben
        //    Rechner seine eigene Ersteinweisung bekommen.
        if (id === willkommenIdRef.current) {
            schreibe('local', WILLKOMMEN_SCHLUESSEL, new Date().toISOString());
            opt.merkeErsteinweisung?.();
        }

        if (ergebnis === 'fertig') opt.markiereAbgeschlossen?.(id);
        else opt.merkeAbbruch?.(id, schrittIndex);
    }, []);

    return { aktiveTourId, starteTour, beendeTour };
};

export { willkommensKapitel, TOUR_SCHLUESSEL, WILLKOMMEN_SCHLUESSEL };
